import type { NextApiResponse } from 'next'
import type { CommunityContext, CommunityRole } from '@/types/community'

/**
 * Community-level authorization.
 *
 * Identity is not authorization. Being signed in, or even holding a seat in
 * one community, grants nothing anywhere else. Every check below reads the
 * membership that `resolveCommunityContext` re-read from the database on THIS
 * request, never a role carried in a cookie or a request body.
 *
 * This is deliberately NOT the platform level. `sellers.role` and the
 * `mta_admin` session in `@/lib/auth/session` remain the platform authority
 * and keep their current meaning; a community owner is not a platform admin.
 */

const RANK: Record<CommunityRole, number> = {
    owner: 5,
    admin: 4,
    moderator: 3,
    listing_manager: 2,
    analyst: 1,
    member: 0
}

/** Roles that may change a community's own settings, branding and categories. */
export const COMMUNITY_SETTINGS_ROLES: Array<CommunityRole> = ['owner', 'admin']

/** Roles that may moderate listings, reports and members inside a community. */
export const COMMUNITY_MODERATION_ROLES: Array<CommunityRole> = ['owner', 'admin', 'moderator']

/** Roles that may create and edit listings on the community's behalf. */
export const COMMUNITY_LISTING_ROLES: Array<CommunityRole> = ['owner', 'admin', 'moderator', 'listing_manager']

/** Roles that may read the community's analytics. */
export const COMMUNITY_ANALYTICS_ROLES: Array<CommunityRole> = ['owner', 'admin', 'moderator', 'analyst']

/**
 * True when the context's membership is active and holds one of `roles`.
 *
 * A platform administrator passes every community check, because the platform
 * console already moderates every tenant. A suspended or banned membership
 * passes none of them, whatever its role says.
 */
/**
 * A seat only counts in the community it was issued for.
 *
 * `resolveCommunityContextForActor` reads the membership FOR the community it is
 * building a context for, so in production a mismatched pair should be
 * impossible. This is the defence-in-depth check that says so anyway: a context
 * assembled by hand, by a future call site, or by a resolver bug must not turn
 * an `owner` seat in community B into authority over community A.
 *
 * That mattered less when each marketplace ran its own bot and its own webhook.
 * Under one shared bot, the `community_id` predicate and this comparison ARE the
 * isolation, so the cheap check is worth having at the capability layer rather
 * than trusting every caller upstream.
 */
const seatBelongsHere = (context: CommunityContext): boolean =>
    context.membership !== null && context.membership.communityId === context.communityId

export const hasCommunityRole = (context: CommunityContext | null, roles: Array<CommunityRole>): boolean => {
    if (!context) return false
    if (context.platformAdmin) return true

    const membership = context.membership

    if (!membership || membership.status !== 'active') return false
    if (!seatBelongsHere(context)) return false

    return roles.includes(membership.role)
}

/** True when the membership is at least as senior as `role`. */
export const hasCommunityRankAtLeast = (context: CommunityContext | null, role: CommunityRole): boolean => {
    if (!context) return false
    if (context.platformAdmin) return true

    const membership = context.membership

    if (!membership || membership.status !== 'active') return false
    if (!seatBelongsHere(context)) return false

    return RANK[membership.role] >= RANK[role]
}

/** True when the caller may sell in this community at all. */
export const canListInCommunity = (context: CommunityContext | null): boolean => {
    if (!context) return false
    if (context.status !== 'approved' || context.storefrontStatus !== 'enabled') return false

    switch (context.directListingPolicy) {
        case 'disabled':
            return false
        case 'staff_only':
            return hasCommunityRole(context, COMMUNITY_LISTING_ROLES)
        case 'members_only':
            return context.platformAdmin || (context.membership?.status === 'active')
        case 'open':
        default:
            return true
    }
}

/** True when the storefront should serve pages for this tenant. */
export const isCommunityPublic = (context: CommunityContext | null): boolean => {
    if (!context) return false

    return context.status === 'approved' && context.storefrontStatus === 'enabled' && context.visibility !== 'private'
}

export class CommunityAccessError extends Error {
    readonly status: number

    constructor(message: string, status = 403) {
        super(message)
        this.name = 'CommunityAccessError'
        this.status = status
    }
}

/**
 * Throwing guard for server code that is not answering an API request
 * directly (services, getServerSideProps helpers).
 */
export const assertCommunityAccess = (context: CommunityContext | null, roles: Array<CommunityRole>): CommunityContext => {
    if (!context) throw new CommunityAccessError('That marketplace could not be found.', 404)
    if (!hasCommunityRole(context, roles)) throw new CommunityAccessError('Your role in this marketplace cannot do that.', 403)

    return context
}

/**
 * API guard. Answers the request itself on failure and returns null, so a
 * caller only has to check for null — the same shape as `requireAdminApi`.
 */
export const requireCommunityRole = (
    res: NextApiResponse,
    context: CommunityContext | null,
    roles: Array<CommunityRole>
): CommunityContext | null => {
    if (!context) {
        res.status(404).json({ ok: false, message: 'That marketplace could not be found.' })

        return null
    }

    if (!hasCommunityRole(context, roles)) {
        res.status(403).json({ ok: false, message: 'Your role in this marketplace cannot do that.' })

        return null
    }

    return context
}
